import React, { useEffect, useMemo } from 'react'

import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticProps } from 'next'
import { ArticleJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'

import { getLayout } from '@/components/layouts/opportunity/Layout'
import { useBreadcrumb } from '@/components/opportunity/common/BreadcrumbV2/context'
import {
  OpportunityDetailShell,
  OpportunityPageLayout,
} from '@/components/opportunity/common/OpportunityDetailShell'
import { normalizeDepartmentName } from '@/components/opportunity/common/transforms'
import { ContractDisplayV2 } from '@/components/opportunity/FederalContract/DisplayV2'
import { ContractDisplayV2Skeleton } from '@/components/opportunity/FederalContract/DisplayV2/Skeleton'

import { FRONTEND_URL } from '@/constants/env'

import { UUID_REGEX } from '@/helpers/id'
import { capitalizeString, removeMarkdownFormatting } from '@/helpers/string'

import { useShare } from '@/hooks/common/useShare'
import {
  federalContractWithoutPresignedUrlsQueryKey,
  fetchFederalContractWithoutPresignedUrls,
  useFederalContractPresignedUrls,
  useSuspenseFederalContractWithoutPresignedUrls,
  useFederalContractLinkedAwards,
} from '@/hooks/federal-contract/useFederalContract'
import { useIndexOpportunity } from '@/hooks/opportunity/useIndexOpportunity'
import { useSimilarOpportunities } from '@/hooks/similar-opportunities'

import type { FederalNotice } from '@/types/federal-contract/FederalContract'
import type { NextPageWithLayout, WithRenderedAt } from '@/types/next'

type FederalContractSolicitationPageProps = WithRenderedAt & {
  id: string
  seoTitle: string
  dehydratedState: ReturnType<typeof dehydrate>
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = (async (context) => {
  const id = context.params?.id
  const notFoundResponse = {
    redirect: {
      destination: '/404',
      permanent: false,
    },
  }

  if (id === undefined || typeof id !== 'string') return notFoundResponse

  // Non-UUID IDs are USASpending IDs — redirect to awards page
  if (!UUID_REGEX.test(id)) {
    return {
      redirect: {
        destination: `/awards/usaspending/${id}`,
        permanent: false,
      },
    }
  }

  try {
    const federalContract = await fetchFederalContractWithoutPresignedUrls(id)

    if (!federalContract || federalContract.notices.length === 0)
      return notFoundResponse

    const queryClient = new QueryClient()
    queryClient.setQueryData(
      federalContractWithoutPresignedUrlsQueryKey(id),
      federalContract,
    )

    const latestNotice = [...federalContract.notices].sort(
      (a, b) =>
        new Date(b.posted_date).getTime() - new Date(a.posted_date).getTime(),
    )[0]

    return {
      props: {
        id,
        seoTitle: latestNotice?.title ?? 'Federal Contract Opportunity',
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        __renderedAt: new Date().toISOString(),
      },
      revalidate: 7200, // revalidate every 2 hours
    }
  } catch (e) {
    if (e instanceof Response && e.status === 404) {
      return notFoundResponse
    }
    if (!(e instanceof Error)) {
      const errorMessage = JSON.stringify(e)
      throw new Error(
        `error occurred while fetching federal contract: ${errorMessage}`,
      )
    }
    throw e
  }
}) satisfies GetStaticProps<FederalContractSolicitationPageProps>

/**
 * Federal Contract Solicitation Page (Public, ISR)
 *
 * Displays solicitation/opportunity details for a federal contract using the V2 design.
 * Data is pre-fetched via getStaticProps, dehydrated into the React Query cache,
 * and revalidated every 2 hours. Linked awards and similar opportunities load
 * progressively client-side.
 *
 * Path: /federal-contracts/[id]
 */
const FederalContractSolicitationPage: NextPageWithLayout = ({
  id,
  seoTitle,
  dehydratedState,
}: FederalContractSolicitationPageProps) => {
  return (
    <OpportunityDetailShell
      seoTitle={seoTitle}
      dehydratedState={dehydratedState}
      errorTitle="Failed to load federal contract"
      notFoundTitle="Contract Not Found"
      backHref="/federal-contracts/search"
      backLabel="Browse Federal Contracts"
      fallback={
        <OpportunityPageLayout>
          <ContractDisplayV2Skeleton />
        </OpportunityPageLayout>
      }
    >
      <FederalContractSolicitationContent id={id} />
    </OpportunityDetailShell>
  )
}

function FederalContractSolicitationContent({ id }: { id: string }) {
  useIndexOpportunity(id, 'federal_contract')

  // The ISR copy can be hours old, so prefer the live record, as long as it has a notice to display.
  const { data: baseFederalContract } =
    useSuspenseFederalContractWithoutPresignedUrls(id)
  const { data: presignedFederalContract } = useFederalContractPresignedUrls(id)
  const federalContract = presignedFederalContract?.notices.length
    ? presignedFederalContract
    : baseFederalContract

  // Fetch linked awards in parallel (loads client-side progressively)
  const { data: linkedAwardsData, isLoading: isLoadingAwards } =
    useFederalContractLinkedAwards(id)

  // Fetch similar opportunities for SEO section (loads client-side)
  const { data: similarOpportunities, isLoading: isSimilarLoading } =
    useSimilarOpportunities('federal_contract', id)

  const { setItems, clearItems } = useBreadcrumb()

  const latestNotice = useMemo<FederalNotice | null>(
    () =>
      federalContract.notices.length > 0
        ? [...federalContract.notices].sort(
            (a, b) =>
              new Date(b.posted_date).getTime() -
              new Date(a.posted_date).getTime(),
          )[0]
        : null,
    [federalContract.notices],
  )

  const earliestNotice = useMemo<FederalNotice | null>(
    () =>
      federalContract.notices.length > 0
        ? [...federalContract.notices].sort(
            (a, b) =>
              new Date(a.posted_date).getTime() -
              new Date(b.posted_date).getTime(),
          )[0]
        : null,
    [federalContract.notices],
  )

  useEffect(() => {
    if (federalContract && latestNotice) {
      const departmentRaw = latestNotice.department || 'Federal Agency'
      const departmentDisplay =
        normalizeDepartmentName(departmentRaw) ?? departmentRaw
      // Use buyer_id for filtering if available, otherwise fall back to query
      const departmentHref = federalContract.buyer_id
        ? `/federal-contracts/search?buyer_ids=${federalContract.buyer_id}`
        : `/federal-contracts/search?query=${encodeURIComponent(departmentRaw)}`
      setItems([
        {
          name: 'Federal Contract Opportunities',
          href: '/federal-contracts/search',
        },
        {
          name: departmentDisplay,
          href: departmentHref,
        },
        {
          name:
            latestNotice.solicitation_number ??
            latestNotice.title ??
            'Contract',
        },
      ])
    }
    return () => clearItems()
  }, [federalContract, latestNotice, setItems, clearItems])

  const handleShare = useShare()

  const title = latestNotice?.title ?? 'Federal Contract Opportunity'

  const seoDescription = useMemo(() => {
    if (!federalContract) return ''
    const originalText =
      federalContract.ai_summary.split('. ').slice(0, 3).join('. ') + '.'
    return removeMarkdownFormatting(originalText)
  }, [federalContract])

  const ogImageUrl = useMemo(() => {
    if (!latestNotice) return ''
    let url = `${FRONTEND_URL}/api/og?title=${encodeURIComponent(
      latestNotice.title ?? 'Federal Contract Opportunity',
    )}`
    if (latestNotice.department) {
      url += `&description=${encodeURIComponent(
        capitalizeString(latestNotice.department),
      )}`
    }
    url += `&subdescription=${encodeURIComponent('Federal Contract Opportunity')}`
    if (
      latestNotice.set_aside_description !== undefined &&
      latestNotice.set_aside_description !== null
    ) {
      url += `&detail=${encodeURIComponent(latestNotice.set_aside_description)}`
    } else if (
      latestNotice.set_aside_code !== undefined &&
      latestNotice.set_aside_code !== null
    ) {
      url += `&detail=${encodeURIComponent(
        `${latestNotice.set_aside_code} Set Aside`,
      )}`
    }
    return url
  }, [latestNotice])

  return (
    <>
      <NextSeo
        title={title}
        description={seoDescription}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: `${FRONTEND_URL}/favicon.ico`,
          },
        ]}
        openGraph={{
          description: seoDescription,
          images: [
            {
              url: ogImageUrl,
              width: 1200,
              height: 632,
              alt: title,
            },
          ],
          siteName: 'Sweetspot',
          title: title,
          url: `${FRONTEND_URL}/federal-contracts/${federalContract.id}`,
        }}
        twitter={{
          handle: '@SweetspotGov',
          site: '@SweetspotGov',
          cardType: 'summary_large_image',
        }}
      />
      <ArticleJsonLd
        url={`${FRONTEND_URL}/federal-contracts/${federalContract.id}`}
        title={title}
        images={[ogImageUrl]}
        datePublished={earliestNotice?.posted_date ?? ''}
        dateModified={latestNotice?.posted_date ?? ''}
        authorName={[
          {
            name: 'Sweetspot',
            url: FRONTEND_URL,
          },
        ]}
        publisherName="Sweetspot"
        publisherLogo={`${FRONTEND_URL}/logo.png`}
        description={federalContract.ai_summary}
        isAccessibleForFree={true}
      />
      {/*
        for some reason, Twitter cards using standard OG tags don't work, need to add this manually;
        https://github.com/garmeeh/next-seo/issues/14;
      */}
      <Head>
        <meta name="robots" content="noarchive" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:image" content={ogImageUrl} />
        <meta property="application-name" content="Sweetspot" />
      </Head>
      <OpportunityPageLayout>
        <ContractDisplayV2
          contract={federalContract}
          linkedAwardsData={linkedAwardsData}
          isLoadingAwards={isLoadingAwards}
          similarOpportunities={similarOpportunities}
          isSimilarLoading={isSimilarLoading}
          onShare={handleShare}
        />
      </OpportunityPageLayout>
    </>
  )
}

FederalContractSolicitationPage.getLayout = getLayout
FederalContractSolicitationPage.requiresAuth = false

export default FederalContractSolicitationPage
